import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, MGMT_LABEL } from "@/lib/api";
import { regRange, isAll, rangeText, dMMM, monYY } from "@/lib/regRange";
import { RegFilter } from "@/components/clients/RegFilter";
import "@/ch.css";

const TZ = (n) => `TZS ${Number(n || 0).toLocaleString("en-US")}`;
const SUB_LABEL = [["active", "Active"], ["paused", "Paused"], ["cancelled", "Cancelled"], ["none", "No subscription recorded"]];
const MODEL_ROWS = [...Object.entries(MGMT_LABEL), ["none", "Not set"]];

const Tile = ({ id, n, l }) => <div className="vt" style={{ cursor: "default" }} data-testid={`sum-tile-${id}`}><b>{n}</b><small>{l}</small></div>;

function Counts({ title, id, rows, data }) {
  return (
    <section className="vl" style={{ padding: 14 }} data-testid={`sum-${id}`}>
      <h4 style={{ margin: "0 0 8px" }}>{title}</h4>
      {rows.filter(([k]) => k !== "none" || id !== "model" || data[k]).map(([k, l]) => (
        <div key={k} className="r sp" style={{ padding: "4px 0" }} data-testid={`sum-${id}-${k}`}><span>{l}</span><b>{data[k] || 0}</b></div>))}
    </section>
  );
}

function Chart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.c + d.s));
  return (
    <section className="vl" style={{ padding: 14, marginTop: 14 }} data-testid="sum-chart">
      <h4 style={{ margin: "0 0 10px" }}>New clients and sign-ups by month</h4>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140 }}>
        {data.map((d) => (
          <div key={d.m} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}
            title={`${monYY(d.m)}: ${d.c} client(s), ${d.s} sign-up(s)`} data-testid={`sum-bar-${d.m}`}>
            <small className="mu" style={{ fontSize: 11 }}>{d.c + d.s || ""}</small>
            <div style={{ width: "100%", maxWidth: 34, height: `${((d.c + d.s) / max) * 100}%`, minHeight: 2, background: "var(--ac)", borderRadius: 4 }} />
            <small className="mu" style={{ fontSize: 10.5, marginTop: 4, whiteSpace: "nowrap" }}>{monYY(d.m)}</small>
          </div>))}
      </div>
      <p className="mu" style={{ margin: "10px 0 0", fontSize: 12.5 }}>Each bar counts the companies that joined that month plus the sign-ups submitted that month.</p>
    </section>
  );
}

export default function ClientsSummary({ rf, setRf, canReview }) {
  const [, setP] = useSearchParams();
  const [d, setD] = useState(null);
  const rg = regRange(rf);
  const all = isAll(rg);
  const rt = rangeText(rg);
  useEffect(() => {
    api.get("/clients/summary", { params: { frm: rg.from || undefined, to: rg.to || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [rg.from, rg.to]);
  return (
    <div className="ch chs" data-testid="clients-summary">
      <RegFilter value={rf} onChange={setRf} />
      <p className="mu" data-testid="sum-range-line">{all ? "Showing every client." : `Showing clients registered ${rt}.`} “Registered” is the date the company joined the Compliance Hub.</p>
      {!d ? <p className="mu" data-testid="sum-loading">Loading…</p> : <>
        <div className="vts" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))" }}>
          <Tile id="total" n={d.total} l="Clients in total" />
          <Tile id="registered" n={d.registered} l={all ? "Registered (all time)" : "Registered in this period"} />
          <Tile id="signups" n={d.signups_waiting} l={`Sign-ups waiting for review${all ? "" : " (this period)"}`} />
          <Tile id="converted" n={d.converted_waiting} l="Converted leads waiting for onboarding" />
          <Tile id="hold" n={d.on_hold} l="Clients with work on hold" />
          <Tile id="deadline" n={d.deadline5} l="Clients with a deadline within 5 days" />
          <Tile id="awaiting" n={TZ(d.awaiting)} l="Awaiting payment from these clients" />
        </div>
        <div className="vcols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Counts title="By management model" id="model" rows={MODEL_ROWS} data={d.by_model} />
          <Counts title="By Compliance Hub subscription" id="sub" rows={SUB_LABEL} data={d.by_sub} />
        </div>
        <Chart data={d.chart} />
        <section className="vl" style={{ padding: 14, marginTop: 14 }} data-testid="sum-clients">
          <h4 style={{ margin: "0 0 6px" }}>Clients registered {rt} <span className="mu">({d.clients.length})</span></h4>
          {d.clients.map((c) => (
            <div key={c.id} className="vr" style={{ cursor: "default" }} data-testid={`sum-client-${c.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}><b translate="no">{c.name}</b>
                <small>Registered {dMMM(c.day)} · {MGMT_LABEL[c.management_model] || "Management model not set"} · {c.open} open obligation(s){c.owed > 0 ? ` · ${TZ(c.owed)} awaiting payment` : ""}</small></div>
              <button className="b g sm" onClick={() => setP({ tab: "cos", co: c.id })} data-testid={`sum-client-open-${c.id}`}>Open</button>
            </div>))}
          {!d.clients.length && <p className="mu" data-testid="sum-clients-empty">No client registered in this period.</p>}
        </section>
        {!!d.signups.length && <section className="vl" style={{ padding: 14, marginTop: 14 }} data-testid="sum-signups">
          <h4 style={{ margin: "0 0 6px" }}>Sign-ups submitted {rt} <span className="mu">({d.signups.length})</span></h4>
          {d.signups.map((s) => (
            <div key={s.id} className="vr" style={{ cursor: "default" }} data-testid={`sum-signup-${s.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}><b translate="no">{s.name}</b><small>{s.contact} · submitted {dMMM(s.day)}{s.from_lead ? " · converted from a lead" : ""}</small></div>
              {canReview && <button className="b g sm" onClick={() => setP({ tab: "onb" })} data-testid={`sum-signup-review-${s.id}`}>Review</button>}
            </div>))}
        </section>}
      </>}
    </div>
  );
}
